import { useState, useEffect } from "react";
import { Alert, Form } from "react-bootstrap";
import { FaDoorOpen, FaKey, FaRegHandPaper, FaUserTie } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import sha256 from "crypto-js/sha256";

import MsgModalSpinner from "../../components/MsgModalSpinner";
import MsgDialog from "../../components/MsgDialog";
import { httpApiPostText } from "../../lib";
import { SetEntornoEmp, USER_LOGOUT } from "../redux/store/Actions";
import "./style.css";
import API_HOST, { HEADERS_JSON, METHOD_POST } from "../../ApiConfig";

// SonarQube: safeJsonParse evita crashes por JSON.parse sin try/catch
const safeJsonParse = <T = any>(str: string, fallback: T | null = null): T | null => {
  try { return JSON.parse(str) as T; } catch { return fallback; }
};

// Define interfaces for better type safety
interface FormData {
  user: string;
  pass: string;
  valid: string;
}

interface UserPayload {
  first_name: string;
  id_user: string;
  last_name: string;
  profile: string;
  token: string;
  user: string;
  idbusiness_group: string;
  id_client?: number | null;
  client_name?: string | null;
}

const PageLogin = () => {
  const [msgDlgShow, setMsgDlgShow] = useState<boolean>(false);
  const [msgDlgText, setMsgDlgText] = useState<string>(
    "Credenciales incorrectas. Por favor intente de nuevo."
  );
  const [msgAlrtUsr, setMsgAlrtUsr] = useState<boolean>(false);
  const [msgAlrtPwd, setMsgAlrtPwd] = useState<boolean>(false);
  const [msgAlrtPwdRegex, setMsgAlrtPwdRegex] = useState<boolean>(false);
  const [inputText, setInputText] = useState<boolean>(false);
  const [formData, setFormData] = useState<FormData>({
    user: "",
    pass: "",
    valid: "login",
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const logo = require("./digitalProf-it.png");

  // Password validation regex
  const PASSWORD_REGEX =
    /^(?=.*?[#?!@$%^&*-])(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[a-zA-Z]).{8,}$/;

  const validatePassword = (password: string): boolean => {
    return PASSWORD_REGEX.test(password);
  };

  const changeText = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = event.target;
    const trimmedValue = value.trim();

    if (id === "pass") {
      setMsgAlrtPwdRegex(!validatePassword(trimmedValue));
    }

    setFormData((prev) => ({
      ...prev,
      [id]: trimmedValue,
    }));

    if (id === "user") setMsgAlrtUsr(false);
    if (id === "pass") setMsgAlrtPwd(false);
  };

  const keyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      void sendForm(event);
    }
  };

  const sendForm = async (e: React.FormEvent) => {
    e.preventDefault();

    const isUserEmpty = !formData.user;
    const isPassEmpty = !formData.pass;

    setMsgAlrtUsr(isUserEmpty);
    setMsgAlrtPwd(isPassEmpty);

    if (isUserEmpty || isPassEmpty) return;

    try {
      setIsLoading(true);

      const loginData = {
        ...formData,
        user: formData.user.trim().toLowerCase(),
        pass: sha256 (formData.pass).toString(),
      };

      const response = await httpApiPostText(
        API_HOST, METHOD_POST,
        { ...HEADERS_JSON },
        loginData
      );

      const data = safeJsonParse(response.message);

      if (!data) {
        const hint =
          response.code === 0
            ? "No se pudo contactar el API. ¿Está XAMPP (Apache + MySQL) encendido y REACT_APP_API_URL apuntando a http://localhost/evaluacion/?"
            : `Respuesta inesperada del servidor (HTTP ${response.code}).`;
        setMsgDlgText(hint);
        setMsgDlgShow(true);
        return;
      }

      if (response.code >= 400 && data.code === 0) {
        setMsgDlgText(
          data.message ||
            "Credenciales incorrectas. Por favor intente de nuevo."
        );
        setMsgDlgShow(true);
        return;
      }

      if (data.code === 3) {
        localStorage.setItem("userAccesos", JSON.stringify(data.payload.accesos));
        navigate("/seleccionarPanel", {
          state: {
            user: data.payload.user,
            token: data.payload.token,
            accesos: data.payload.accesos,
          },
        });
      } else if (data.code === 1 || data.code === 2) {
        const userPayload: UserPayload = {
          first_name: data.payload.user.first_name,
          id_user: data.payload.user.id_user,
          last_name: data.payload.user.last_name,
          profile: data.payload.user.profile,
          token: data.payload.token,
          user: formData.user,
          idbusiness_group: data.payload.user.idbusiness_group,
          id_client: data.payload.user.id_client ?? null,
          client_name: data.payload.user.client_name ?? null,
        };
        dispatch(SetEntornoEmp(userPayload));
        localStorage.setItem("user", JSON.stringify(data.payload));
        localStorage.setItem("authToken", data.payload.token);
        localStorage.setItem("userData", JSON.stringify({
          ...data.payload.user,
          id_client: data.payload.user.id_client ?? null,
          client_name: data.payload.user.client_name ?? null,
        }));
        navigate("/home");
      } else {
        setMsgDlgText(
          data?.message || "Credenciales incorrectas. Por favor intente de nuevo."
        );
        setMsgDlgShow(true);
      }
    } catch (error) {
      setMsgDlgText("Error de conexión. Intente más tarde.");
      setMsgDlgShow(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    document.body.dataset.page = "login";
    if (!localStorage.getItem("authToken")) {
      dispatch(USER_LOGOUT());
    }
    return () => {
      delete document.body.dataset.page;
    };
  }, [dispatch]);

  const CODE_BG = `// Encrypting payload... // SHA-256 Hash verified. // Token generation started. // Digital Profit SAS © 2026
const digitalProfit = { mission: "Innovación y Crecimiento", tech: ["React", "Node.js", "AWS"],
  init: () => new Promise((resolve) => { console.log("System loaded..."); resolve(true);
}) }; async function evaluatePerformance(user) { const data = await API.getMetrics(user);
  return { score: data.score, feedback: data.comments }; } // Secure connection established //
// Encrypting payload... // SHA-256 Hash verified. // Token generation started. // Digital Profit
SAS © 2026 const digitalProfit = { mission: "Innovación y Crecimiento", tech: ["React", "Node.js",
"AWS"], init: () => new Promise((resolve) => { console.log("System loaded..."); resolve(true);
}) }; async function evaluatePerformance(user) { const data = await API.getMetrics(user);
  return { score: data.score, feedback: data.comments }; } // Secure connection established //
// Encrypting payload... // SHA-256 Hash verified. // Token generation started. // Digital Profit`;

  return (
    <>
      <div className="login-split-wrapper">
        {/* Panel izquierdo oscuro */}
        <div className="login-left-panel">
          <div className="code-bg">{CODE_BG}</div>
          <img src={logo} alt="Digital Profit Logo" className="brand-logo" />
          <div className="login-left-tagline">
            <h1>
              SISTEMA DE<br />
              <span>EVALUACIÓN</span><br />
              DE DESEMPEÑO
            </h1>
            <p>Plataforma corporativa para la gestión del talento humano y el crecimiento estratégico.</p>
          </div>
        </div>

        {/* Panel derecho blanco */}
        <div className="login-right-panel">
          <h2>Bienvenido</h2>
          <p className="login-subtitle">Ingrese sus credenciales para continuar</p>

          <Form onSubmit={sendForm} style={{ width: "100%" }}>
            <Form.Group className="mb-3">
              <div className="login-field-label">
                <FaUserTie size={11} /> Usuario
              </div>
              <Form.Control
                type="text"
                placeholder="Ingrese su usuario"
                id="user"
                onChange={changeText}
                value={formData.user}
                aria-invalid={msgAlrtUsr}
              />
              <Alert variant="danger" show={msgAlrtUsr} className="p-1 mt-1" style={{ fontSize: "0.82rem" }}>
                <FaRegHandPaper className="me-1" /> Usuario inválido
              </Alert>
            </Form.Group>

            <Form.Group className="mb-4">
              <div className="login-field-label">
                <FaKey size={11} /> Contraseña
              </div>
              <Form.Control
                type={inputText ? "text" : "password"}
                placeholder=""
                id="pass"
                onChange={changeText}
                onKeyUp={keyPress}
                value={formData.pass}
              />
              <Alert variant="danger" show={msgAlrtPwd} className="p-1 mt-1" style={{ fontSize: "0.82rem" }}>
                <FaRegHandPaper className="me-1" /> Ingresa una contraseña válida
              </Alert>
            </Form.Group>

            <button type="submit" className="login-btn-submit" disabled={isLoading}>
              <FaDoorOpen size={16} />
              {isLoading ? "Ingresando..." : "Iniciar Sesión"}
            </button>

            {msgDlgShow && (
              <div className="text-center mt-3" style={{ color: "#999", fontSize: "0.82rem" }}>
                <p className="mb-0">¿Problemas para acceder?</p>
                <p>Contacte con el administrador del sitio.</p>
              </div>
            )}
          </Form>

          <div className="login-right-footer" style={{ fontWeight: 'bold' }}>
            © DIGITAL PROFIT SAS COLOMBIA {new Date().getFullYear()}
          </div>
        </div>
      </div>

      <MsgDialog
        Show={msgDlgShow}
        Title="Error de autenticación"
        Message={msgDlgText}
        BtnOkName="Entendido"
        BtnNokName=""
        HandlerClickOk={() => setMsgDlgShow(false)}
        HanlerdClickNok={null}
      />
      <MsgModalSpinner
        Show={isLoading}
        text="Cargando valores..."
        color="#FF7588"
      />
    </>
  );
};

export default PageLogin;